import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useChangeSubscriptionStateMutation } from 'src/apiClients';
import { EntityState } from 'src/apiClients/queries/entity-state';

import { i18n } from './ChangeSubscriptionDialog.i18n';

export type ChangeSubscriptionDialogProps = {
    open: boolean;
    subscriptionId: string;
    tenantId: string;
    newState: EntityState;
    onClose: () => void;
};

export const ChangeSubscriptionDialog = ({
    subscriptionId,
    tenantId,
    open,
    newState,
    onClose,
}: ChangeSubscriptionDialogProps) => {
    const intl = useIntl();
    const { enqueueSnackbar } = useSnackbar();
    const [changeSubscriptionState, { isLoading }] = useChangeSubscriptionStateMutation();

    const handleActiveDeactive = useCallback(async () => {
        try {
            await changeSubscriptionState({ subscriptionId, tenantId, newState }).unwrap();
            onClose();
        } catch {
            enqueueSnackbar(intl.formatMessage(i18n.changeError), { variant: 'error' });
        }
    }, [
        subscriptionId,
        tenantId,
        newState,
        changeSubscriptionState,
        enqueueSnackbar,
        intl,
        onClose,
    ]);

    return (
        <Dialog open={open} onClose={isLoading ? undefined : onClose}>
            <DialogTitle>{intl.formatMessage(i18n.title)}</DialogTitle>
            <DialogContent>
                {newState === EntityState.ACTIVE
                    ? intl.formatMessage(i18n.activateDescription)
                    : intl.formatMessage(i18n.deactivateDescription)}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>
                    {intl.formatMessage(i18n.cancelButtonText)}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleActiveDeactive}
                    color="primary"
                    disabled={isLoading}
                >
                    {newState === EntityState.ACTIVE
                        ? intl.formatMessage(i18n.activateButtonText)
                        : intl.formatMessage(i18n.deactivateButtonText)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
